import { RuntimeWidget } from 'client/types';
import withErrorBoundary from 'client/widget-components/components/ErrorBoundary/withErrorBoundary';
import useEditor from 'client/widget-components/customHooks/useEditor';
import { useIsHydrated } from 'client/widget-components/customHooks/useIsHydrated';
import React, { Suspense, lazy, useMemo, useState } from 'react';
import { API_URL } from './api';
import BookerSkeleton from './components/BookerSkeleton';
import BookingConfirmation from './components/BookingConfirmation';
import BookingConfirmationDryRun from './components/BookingConfirmationDryRun';
import BookingMarkup from './components/BookingMarkup';
import { CalBookingWrapper, containerQuery } from './styles';
import { Booking, DesignStyle } from './types';
import { getBookerClassNames } from './utils';

const loadModule = () => import('@calcom/atoms');

const Booker = lazy(() =>
    loadModule().then((module) => ({ default: module.Booker }))
);
const CalProvider = lazy(() =>
    loadModule().then((module) => ({ default: module.CalProvider }))
);

export interface CalBookingProps {
    clientId: string;
    userName: string;
    eventSlug: string;
    localeCode?: any;
    apiUrl?: string;
    teamId: number;
    featureMetadata: {
        isNativeBookingAvailable: boolean;
        isNativeBookingPublishingAvailable: boolean;
        isPaidNativeBookingAvailable: boolean;
    };
    widgetId: string;
    designStyle: DesignStyle | null;
}

const DEFAULT_LOCALE = 'en';
const RuntimeCalBooking: RuntimeWidget = (props: CalBookingProps) => {
    const {
        localeCode = DEFAULT_LOCALE,
        apiUrl = API_URL,
        eventSlug,
        clientId,
        userName,
        teamId,
        featureMetadata,
        widgetId,
        designStyle: designStyleProp,
    } = props;

    const [isLoading, setIsLoading] = useState(true);
    const [rescheduleUid, setRescheduleUid] = useState<string | null>(null);
    const [confirmationData, setConfirmationData] = useState<Booking | null>(
        null
    );
    const [editor] = useState(useEditor());
    const hydrated = useIsHydrated();

    const designStyle = designStyleProp || DesignStyle.SITE_THEME;

    const bookerClassNames = useMemo(
        () => getBookerClassNames(designStyle),
        [designStyle]
    );

    if (!eventSlug) {
        if (editor.isInPreviewOrEditor()) {
            return <BookingMarkup />;
        }
        return null;
    }

    const clearConfirmationData = () => {
        setConfirmationData(null);
    };

    const rescheduleBooking = () => {
        if (!confirmationData?.uid) {
            return;
        }
        setRescheduleUid(confirmationData.uid);
        clearConfirmationData();
    };

    const onBookingSuccess = (res: { data: Booking }) => {
        setConfirmationData(res.data);
        setRescheduleUid(null);
    };

    const isBookingDryRun =
        editor.isInPreviewOrEditor() ||
        !featureMetadata.isNativeBookingPublishingAvailable;

    const showDryRunConfirmation = confirmationData && isBookingDryRun;
    const showConfirmation = confirmationData && !isBookingDryRun;
    // We want to render the booker only after the component is hydrated on client side since it's a lazy loaded component and we want to keep the server and client rendering consistent.
    const shouldRenderBooker = hydrated;

    return (
        <CalBookingWrapper
            data-auto='ssr-cal-booking-widget'
            className={`${designStyle} ${
                isLoading ? 'booker-loading' : 'booker-ready'
            }`}
        >
            <style
                dangerouslySetInnerHTML={{ __html: containerQuery(widgetId) }}
            />
            <div className='booker-wrapper'>
                {shouldRenderBooker && (
                    <Suspense>
                        <CalProvider
                            clientId={clientId}
                            options={{ apiUrl }}
                            language={localeCode}
                        >
                            {showConfirmation && (
                                <BookingConfirmation
                                    confirmationData={confirmationData}
                                    closeConfirmation={clearConfirmationData}
                                    rescheduleBooking={rescheduleBooking}
                                />
                            )}
                            {showDryRunConfirmation && (
                                <BookingConfirmationDryRun
                                    closeConfirmation={clearConfirmationData}
                                />
                            )}
                            <Booker
                                key={
                                    confirmationData
                                        ? 'booker-confirmed'
                                        : 'booker-initial'
                                }
                                eventSlug={eventSlug}
                                customClassNames={bookerClassNames}
                                onCreateBookingSuccess={onBookingSuccess}
                                {...(teamId
                                    ? { isTeamEvent: true, teamId }
                                    : { username: userName })}
                                rescheduleUid={rescheduleUid || undefined}
                                isBookingDryRun={isBookingDryRun}
                                onBookerStateChange={(bookerState) => {
                                    if (
                                        bookerState.state === 'selecting_time'
                                    ) {
                                        setIsLoading(false);
                                    }
                                }}
                            />
                        </CalProvider>
                    </Suspense>
                )}
                {isLoading && <BookerSkeleton />}
            </div>
        </CalBookingWrapper>
    );
};

export default withErrorBoundary({
    Comp: RuntimeCalBooking,
    logProps: true,
    componentName: 'CalBooking',
    additionalInfoToLog: {
        tag: 'booking',
    },
});
